import React, { useState } from 'react';
import { Table, Input, Space, Card, Tag, Button, Select } from 'antd';
import { useQuery } from '@apollo/client';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAllCreditProducts } from '../../graphql/queries';
import '../Audios/Audios.css';

const { Option } = Select;

const CreditProducts = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filter, setFilter] = useState({
    type: null,
    name: null,
    startDate: null,
    endDate: null,
  });

  const { loading, data } = useQuery(getAllCreditProducts, {
    variables: {
      filter: {
        type: filter.type,
        name: filter.name,
        startDate: filter.startDate,
        endDate: filter.endDate,
      },
      pagination: {
        skip: (pagination.current - 1) * pagination.pageSize,
        take: pagination.pageSize,
      },
    },
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const productTypes = [
    { value: 'ONE_TIME', label: 'One Time' },
    { value: 'YEARLY', label: 'Yearly' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'LIFETIME', label: 'Lifetime' },
    { value: 'PROMO', label: 'Promo' },
  ];

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (
        <Tag color={type === 'ONE_TIME' ? 'purple' : 'blue'}>
          {type || 'N/A'}
        </Tag>
      ),
      filters: productTypes.map(type => ({ text: type.label, value: type.value })),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => amount?.toLocaleString() || 'N/A',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price) => formatCurrency(price) || 'N/A',
    },
    {
      title: 'Limit',
      dataIndex: 'limit',
      key: 'limit',
      width: 120,
      render: (limit) => limit === 0 ? 'Unlimited' : limit?.toLocaleString(),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => formatDate(date),
    },
  ];

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilter(prev => ({ ...prev, name: value || null }));
    setPagination({ ...pagination, current: 1 });
  };

  const handleTypeChange = (value) => {
    setFilter(prev => ({ ...prev, type: value || null }));
    setPagination({ ...pagination, current: 1 });
  };

  return (
    <div className="audios-container">
      <div className="audios-header">
        <p className="audios-breadcrumb">Pages / Credit Products</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="audios-title">Credit Products</h1>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/credit-products/create')}
          >
            Create Product
          </Button>
        </div>
      </div>

      <Card className="audios-card">
        <div className="filters-container">
          <Space wrap>
            <Select
              placeholder="Filter by type"
              allowClear
              style={{ width: 150 }}
              onChange={handleTypeChange}
            >
              {productTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
            <Input
              placeholder="Search by name"
              allowClear
              prefix={<SearchOutlined />}
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={(e) => handleSearch(e.target.value)}
            />
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data?.getAllCreditProducts?.items}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total: data?.getAllCreditProducts?.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} credit products`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => navigate(`/credit-products/${record.id}`),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>
    </div>
  );
};

export default CreditProducts; 